import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  styled,
} from '@mui/material';
import moment from 'moment';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import getAxiosInstance from 'api';
import CircularProgressBar from 'ui-component/CircularProgressBar';
import toast from 'react-hot-toast';
import {
  appListPayloadDefaults,
  dashboardQuickRanges,
  getDateRangeFromQuickOption,
  getDefaultDashboardDateRange,
  mapAppListItemsForCustomerSelect,
} from 'utils/common';

const StyledCard = styled(Card)(() => ({
  padding: '24px',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.1)',
}));

/** API customer dropdown: min height on the scrollable list + paper */
const customerSelectMenuProps = {
  PaperProps: {
    sx: {
      minHeight: 260,
      maxHeight: 420,
    },
  },
  MenuListProps: {
    sx: {
      minHeight: 240,
      maxHeight: 380,
      overflow: 'auto',
    },
  },
};

const PRODUCT_REVENUE_API =
  'adminOrchestrator/dashboard/getProductWiseRevenueByPartner';

const PRODUCT_ORDER = [
  { key: 'creditAssessment', title: 'Credit Assessment' },
  { key: 'cac', title: 'CAC' },
  { key: 'nin', title: 'NIN' },
  { key: 'bvn', title: 'BVN' },
];

/** Values for `kycType` on getProductWiseRevenueByPartner — align with backend if names differ. */
const KYC_TYPE_OPTIONS = [
  { label: 'All products', value: 'ALL' },
  { label: 'Credit Assessment', value: 'creditAssessment' },
  { label: 'CAC', value: 'cac' },
  { label: 'NIN', value: 'nin' },
  { label: 'BVN', value: 'bvn' },
];

function formatNgn(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) {
    return '₦0';
  }
  return `₦${n.toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function toMoney(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function toCount(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** First defined numeric field from object (API naming may vary). */
function firstNumber(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

function aggregateRevenueFromApi(data, kycFilter = 'ALL') {
  const productRows =
    kycFilter === 'ALL'
      ? PRODUCT_ORDER
      : PRODUCT_ORDER.filter((p) => p.key === kycFilter);

  let manualSum = 0;
  let digitalSum = 0;

  const byProduct = productRows.map(({ key, title }) => {
    const p = data?.[key] || {};
    const mr = toMoney(p.manualRevenue);
    const dr = toMoney(p.digitalRevenue);
    manualSum += mr;
    digitalSum += dr;
    const revenue = mr + dr;
    const manualCalls = toCount(p.manualCalls);
    const digitalCalls = toCount(p.digitalCalls);
    const totalCalls = manualCalls + digitalCalls;

    const productPrice = firstNumber(p, [
      'productPrice',
      'unitPrice',
      'price',
      'averagePrice',
      'productWisePrice',
    ]);
    const cpcFromApi = firstNumber(p, [
      'cpc',
      'CPC',
      'costPerCall',
      'costPerConsent',
      'averageCpc',
    ]);
    const cpc =
      cpcFromApi != null
        ? cpcFromApi
        : totalCalls > 0
          ? revenue / totalCalls
          : 0;

    return {
      key,
      title,
      revenue,
      manualCalls,
      digitalCalls,
      totalCalls,
      productPrice,
      cpc,
    };
  });

  const totalRevenue = manualSum + digitalSum;
  const totalCallsAll = byProduct.reduce((s, pr) => s + pr.totalCalls, 0);
  const cpcFromRoot = firstNumber(data, [
    'cpc',
    'CPC',
    'averageCpc',
    'costPerCall',
  ]);
  const totalCpc =
    cpcFromRoot != null
      ? cpcFromRoot
      : totalCallsAll > 0
        ? totalRevenue / totalCallsAll
        : 0;

  return {
    totalRevenue,
    manualTotalRevenue: manualSum,
    digitalTotalRevenue: digitalSum,
    byProduct,
  };
}

const RevenueDashboard = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [callsQuickRange, setCallsQuickRange] = useState('');
  const [kycType, setKycType] = useState('ALL');
  const [selectedDate, setSelectedDate] = useState(() => getDefaultDashboardDateRange());
  const [rawRevenue, setRawRevenue] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    setLoadingCustomers(true);
    getAxiosInstance(
      'adminOrchestrator/app/list',
      'post',
      appListPayloadDefaults,
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          const items = res?.data?.data?.items || [];
          setCustomers(mapAppListItemsForCustomerSelect(items));
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || 'Failed to load API customers'
        );
      })
      .finally(() => setLoadingCustomers(false));
  }, []);

  useEffect(() => {
    setLoadingStats(true);
    const payload = {
      kycType,
      startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
      endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
    };
    if (selectedCustomer) {
      payload.businessPartnerId = Number(selectedCustomer);
    }

    getAxiosInstance(
      PRODUCT_REVENUE_API,
      'post',
      payload,
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200 && res?.data?.data) {
          setRawRevenue(res.data.data);
        } else {
          setRawRevenue(null);
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || 'Failed to load revenue data'
        );
        setRawRevenue(null);
      })
      .finally(() => setLoadingStats(false));
  }, [selectedCustomer, selectedDate, kycType]);

  const metrics = useMemo(
    () => aggregateRevenueFromApi(rawRevenue || {}, kycType),
    [rawRevenue, kycType]
  );

  const totalRow = [
    {
      key: 'total',
      header: 'Total Revenue',
      title: 'Total',
      value: formatNgn(metrics.totalRevenue),
    },
    {
      key: 'manual-total',
      header: 'Manual Total',
      title: 'Manual Revenue',
      value: formatNgn(metrics.manualTotalRevenue),
    },
    {
      key: 'digital-total',
      header: 'Digital Total',
      title: 'Digital Revenue',
      value: formatNgn(metrics.digitalTotalRevenue),
    },
  ];

  const showLoading = loadingStats;

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: 500, color: '#212120' }}>
          Revenue Dashboard
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'nowwrap',
          }}
        >
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <Select
              value={selectedCustomer}
              onChange={(e) => {
                const id = String(e.target.value);
                setSelectedCustomer(id);
                if (id) {
                  setSelectedDate(getDefaultDashboardDateRange());
                }
              }}
              displayEmpty
              disabled={loadingCustomers}
              MenuProps={customerSelectMenuProps}
              sx={{ backgroundColor: '#fff' }}
            >
              <MenuItem value="">Select API Customer</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={String(customer.id)}>
                  {customer.companyName || customer.emailAddress || customer.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={kycType}
              onChange={(e) => setKycType(e.target.value)}
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              {KYC_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={callsQuickRange}
              onChange={(e) => {
                const value = e.target.value;
                setCallsQuickRange(value);
                if (value) {
                  const range = getDateRangeFromQuickOption(value);
                  if (range) {
                    setSelectedDate([range.start, range.end]);
                  }
                }
              }}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              <MenuItem value="">Quick Range</MenuItem>
              {dashboardQuickRanges.map((range) => (
                <MenuItem key={range.value} value={range.value}>
                  {range.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            value={selectedDate}
            onChange={(value) => {
              if (!value) return;
              setSelectedDate([value[0], value[1]]);
            }}
            showOneCalendar
          />
        </Box>
      </Box>

      {showLoading ? (
        <StyledCard>
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress color="secondary" />
          </Box>
        </StyledCard>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <StyledCard>
            <Typography
              variant="h4"
              sx={{ fontWeight: 500, color: '#212120', mb: 2 }}
            >
              Total Revenue
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {totalRow.map((row) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={row.key}
                  sx={{ textAlign: 'center' }}
                >
                  <Box sx={{ maxWidth: 220, margin: '0 auto' }}>
                    <CircularProgressBar
                      width={100}
                      size={150}
                      header={row.header}
                      title={row.title}
                      value={row.value}
                      progress={100}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </StyledCard>

          <StyledCard>
            <Typography
              variant="h4"
              sx={{ fontWeight: 500, color: '#212120', mb: 2 }}
            >
              Revenue by Product
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {metrics.byProduct.map((product) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={product.key}
                  sx={{ textAlign: 'center' }}
                >
                  <Box sx={{ maxWidth: 240, margin: '0 auto' }}>
                    <CircularProgressBar
                      width={100}
                      size={150}
                      header={product.title}
                      title="Revenue"
                      value={formatNgn(product.revenue)}
                      progress={100}
                    />
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        sx={{ fontSize: '13px', color: 'text.secondary' }}
                      >
                        CPC —{' '}
                        {product.productPrice != null
                          ? formatNgn(product.productPrice)
                          : '—'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </StyledCard>
        </Box>
      )}
    </Box>
  );
};

export default RevenueDashboard;
